// @ts-nocheck
import { AccessControl } from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "../../../customHooks/useDebounceHook";
import useGetHook from "../../../customHooks/useGetHook";
import CreateIcon from "../../../Icons/CreateIcon";
import Modal from "../Modal";
import Spinner from "../Spinner";
import DownloadExcel from "./DownloadExcel";
import Template from "./loading";
import APIS from "../../../constant/EndPoint";
import { useVirtualizer } from "@tanstack/react-virtual";
import React from "react";
import { TableSettingsLayout } from "@careinfo/layout";
import usePatchHook from "../../../customHooks/usePatchHook";
import { classNames, responsiveClassName } from "../../../utils/classNames";
import UploadIcon from "../UploadIcon";

interface IDataTable {
  url: string;
  queryKey: string;
  columns: any;
  addNewPagePath?: string;
  tableName: string;
  params?: any;
  scanComponent?: any;
  actionMenu?: any;
  setRerender?: any;
  isUpload?: boolean;
  isDownload?: boolean;
}

function DataTable(props: IDataTable) {
  const {
    url,
    queryKey,
    columns,
    addNewPagePath,
    scanComponent,
    actionMenu,
    params: otherParams,
    isUpload = false,
    tableName,
    isDownload = false,
  } = props;

  const [searchParams, setSearchParms] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });

  const { data: tableConfigData, isLoading: isTableConfigLoading } = useGetHook(
    {
      url: `${APIS.TABLE_CONFIG}${tableName}/`,
      queryKey: [tableName],
      params: {},
    }
  );

  const [limit, setLimit] = useState(10);
  const page = parseInt(searchParams.get("page") ?? "0");
  const searchQuery = searchParams.get("searchQuery");
  const selectedItems = searchParams.get("selectedItems");
  const debouncesearch = useDebounce(searchQuery);
  const navigate = useNavigate();
  const [countSelectedItems, setSelectedItems] = useState([]);
  const [isOpenTableSetting, setIsOpenTableSettings] = useState(false);
  const { register, setError, handleSubmit } = useForm({});

  useEffect(() => {
    const items = selectedItems?.split(",");
    setSelectedItems(items?.length);
  }, [selectedItems]);

  useEffect(() => {
    if (tableConfigData?.page_limit) {
      setLimit(tableConfigData?.page_limit);
    }
  }, [tableConfigData]);

  const {
    data: serverData,
    isLoading,
    isFetching,
  } = useGetHook({
    queryKey: [
      queryKey,
      `${queryKey}-${limit}-${page}-${debouncesearch}-${JSON.stringify(
        otherParams
      )}`,
    ],
    url: url,
    params: {
      limit: limit,
      offset: limit * page,
      search: debouncesearch,
      ...otherParams,
    },
  });
  //  const { data: pickupData, isLoading: ispickupDataloading } = useGetHook({
  //   queryKey: ["pickup-count"],
  //   url: `${APIS.PICKUPCOUNT}`,
  //   params: {},
  // });

  const [data, setData] = useState([]);

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData([...serverData?.results] ?? []);
      if (searchParams.get("selectAllItems") === "1") {
        let _newItems = serverData?.results
          ?.map((item) => item.id)
          .join(",")
          .toString();
        setSearchParms({
          page: page,
          selectedItems: _newItems,
          selectAllItems: searchParams.get("selectAllItems"),
          searchQuery: searchQuery,
        });
      }
    }
  }, [serverData]);

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const { mutateAsync: updateTableSettings } = usePatchHook({
    queryKey: [tableName],
  });

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 80,
    getScrollElement: () => tableContainerRef.current,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const handleAddPageRoute = () => {
    navigate(addNewPagePath);
  };

  useEffect(() => {
    table.setPageSize(limit);
  }, [limit, tableConfigData]);

  const handleNextPage = () => {
    searchParams.set("page", page + 1);
    setSearchParms(searchParams);
  };

  const handlePreviousPage = () => {
    searchParams.set("page", page - 1);
    setSearchParms(searchParams);
  };

  const handleLastpage = () => {
    searchParams.set("page", Math.floor(serverData?.count / limit));
    setSearchParms(searchParams);
  };

  const handleFirstPage = () => {
    searchParams.set("page", 0);
    setSearchParms(searchParams);
  };

  const handleSearch = (value) => {
    searchParams.set("searchQuery", value);
    searchParams.set("page", 0);
    setSearchParms(searchParams);
  };

  const handleUploadExcel = () => {
    navigate("/bulk-upload");
  };

  return isLoading ? (
    <Template addNewPath={addNewPagePath} limit={limit} />
  ) : (
    <>
      {addNewPagePath && (
        <div className={tw`w-full flex justify-between`}>
          <AccessControl allowedPermissions={["can_create_consignment"]}>
            <button
              onClick={handleAddPageRoute}
              className={tw`p-2 flex gap-x-2 bg-primary-light my-2 rounded text-primary focus:outline-none`}
            >
              <CreateIcon color={"var(--primary)"} />
              Add New
            </button>
          </AccessControl>
          {countSelectedItems ? (
            <div
              className={tw`text-primary gap-x-4 flex items-center justify-center`}
            >
              <span>Selected Items:{countSelectedItems ?? 0}</span>
              <button
                className={tw`focus:outline-none px-4 py-1 border-1 border-primary rounded-md flex items-center justify-center`}
                onClick={() => {
                  const clonedParams = new URLSearchParams(searchParams);
                  clonedParams.delete("selectAllItems");
                  clonedParams.delete("selectedItems");
                  setSearchParms(clonedParams);
                }}
              >
                Unselect All
              </button>
            </div>
          ) : null}
          {scanComponent}
        </div>
      )}

      <div
        className={tw`w-full bg-white rounded-sm mb-8`}
        style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" }}
      >
        <div
          className={tw`w-full grid grid-cols-1 gap-4 md:flex justify-between items-center p-2`}
        >
          <div className={tw`mr-2`}>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery ?? ""}
              onChange={(e) => handleSearch(e?.target?.value)}
              className={tw`border w-full border-1 border-primary focus:outline-none p-1`}
            />
          </div>
          <div
            className={responsiveClassName(
              "text-primary w-full  flex flex-col gap-y-2 ",
              "text-primary w-full justify-end items-center flex gap-x-4 "
            )}
          >
            <div
              className={tw`flex items-center gap-x-1 w-full cursor-pointer`}
            >
              {actionMenu}
            </div>

            <AccessControl
              allowedPermissions={["can_download"]}
              children={
                <>
                  {isUpload && (
                    <button
                      className="flex gap-x-1 cursor-pointer text-primary focus:outline-none"
                      onClick={handleUploadExcel}
                    >
                      <UploadIcon color={"var(--primary)"} />
                      Upload
                    </button>
                  )}
                </>
              }
            />
            <TableSettingsLayout
              isOpenTableSetting={isOpenTableSetting}
              setIsOpenTableSettings={setIsOpenTableSettings}
              tableName={tableName}
              defaultColumns={columns}
              onSettingsLoaded={(settings) => {
                if (settings?.page_limit != null) {
                  setLimit(settings.page_limit);
                  searchParams.set("limit", settings.page_limit.toString());
                  setSearchParms(searchParams);
                }
              }}
            />
          </div>
        </div>

        <div
          style={{
            overflow: "auto",
            position: "relative",
            minHeight: "100px",
            maxHeight: "calc(100vh - 250px)",
          }}
          ref={tableContainerRef}
        >
          <table className={tw`w-full relative`}>
            <thead
              className={tw`bg-primary text-white uppercase sticky top-0 z-[5]`}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className={tw`flex w-full justify-between z-5`}
                >
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: index === 0 ? "" : header.getSize(),
                      }}
                      className={classNames(
                        index === 0
                          ? tw``
                          : headerGroup.headers?.length - 1 === index
                          ? tw` text-left w-[8rem]`
                          : tw``,
                        tw`py-[5px] text-left px-5 whitespace-nowrap flex`
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody
              style={{
                display: "grid",
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index] as Row<Person>;
                return (
                  <tr
                    data-index={virtualRow.index}
                    ref={(node) => {
                      if (node) {
                        requestAnimationFrame(() => {
                          rowVirtualizer.measureElement(node);
                        });
                      }
                    }}
                    key={row.id}
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className={tw`group even:bg-gray-50 odd:bg-white hover:bg-gray-100 hover:transition-All w-full flex justify-between items-center absolute`}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: index === 0 ? "" : cell.column.getSize(),
                          }}
                          className={tw`py-[10px] text-left px-5 `}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>

        <div
          className={tw`flex bg-white py-2 my-4 w-full justify-center gap-4`}
        >
          <button
            className={tw`border hidden md:block rounded p-1`}
            onClick={() => handleFirstPage()}
            disabled={!serverData?.previous}
          >
            {"<<"}
          </button>
          <button
            className={tw`border rounded p-1`}
            onClick={() => handlePreviousPage()}
            disabled={!serverData?.previous || page === 0}
          >
            {"<"}
          </button>
          <button
            className={tw`border rounded p-1`}
            onClick={() => handleNextPage()}
            disabled={
              !serverData?.next ||
              page + 1 >= Math.ceil(serverData?.count / limit)
            }
          >
            {">"}
          </button>
          <button
            className={tw`border hidden md:block rounded p-1`}
            onClick={() => handleLastpage()}
            disabled={!serverData?.next}
          >
            {">>"}
          </button>
          <span className={tw`flex items-center gap-1`}>
            <div>Page</div>
            <strong>
              {page + 1} of {Math.ceil(serverData?.count / limit)}
            </strong>
          </span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              searchParams.set("limit", e?.target?.value);
              setSearchParms(searchParams);
            }}
          >
            {[10, 50, 100, 200, 500].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <p className={tw`mt-1`}>All: {serverData?.count}</p>
        </div>
      </div>
    </>
  );
}

export default DataTable;
